import React, { useState, useEffect } from 'react';
import { getFarmData, getFarmImages } from '../services/api';
import ImageGallery from './ImageGallery';
import LoadingSpinner from './LoadingSpinner';

function FarmDetails({ farmId, user }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [farmData, setFarmData] = useState(null);
  const [images, setImages] = useState([]);
  const [hasImages, setHasImages] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        // Fetch farm data
        const farmResponse = await getFarmData(farmId, user);
        
        if (farmResponse.data) {
          setFarmData(farmResponse.data);
        } else {
          throw new Error('Unable to retrieve farm data');
        }
      } catch (err) {
        console.error('Error fetching farm data:', err);
        setError('Error loading farm information. Please try again later.');
      } finally {
        setLoading(false);
      }
    }

    async function fetchImages() {
      try {
        // Fetch images
        const imagesResponse = await getFarmImages(farmId, user);
        
        if (imagesResponse.images) {
          setImages(imagesResponse.images);
          setHasImages(true);
        }
      } catch (err) {
        console.error('Error fetching images:', err);
        // We don't set an error here since missing images is acceptable
        setHasImages(false);
      }
    }

    if (farmId) {
      fetchData();
      fetchImages();
    }
  }, [farmId, user]);

  const handleViewMap = () => {
    if (farmData?.map_link) {
      window.open(farmData.map_link, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <h1 className="text-xl text-center p-2 font-bold text-gray-900">Error!</h1>
        <h2 className="text-center text-gray-700 p-1">{error}</h2>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4">
      <h1 className="text-xl text-center p-2 font-bold text-gray-900">
        {farmData?.dlb_name || 'Location Information'}
      </h1>
      
      <h2 className="text-center text-gray-700 p-1">
        {farmData?.address_street} {farmData?.address_city} {farmData?.address_province} {farmData?.address_postal_code}
      </h2>
      
      {farmData?.driver_notes && (
        <h3 className="text-center text-black-700 font-semibold p-1 mt-2">
          Driver Notes: {farmData.driver_notes}
        </h3>
      )}

      <div className="text-center py-8">
        <div className="inline-flex flex-wrap justify-center gap-4">
          {hasImages ? (
            <ImageGallery 
              images={images} 
              farmId={farmId}
              user={user}
            />
          ) : (
            <button
              className="bg-gray-400 text-white font-semibold py-2 px-6 rounded shadow cursor-not-allowed"
              disabled
            >
              No Images
            </button>
          )}
          
          <button
            onClick={handleViewMap}
            className={`bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-6 rounded shadow ${!farmData?.map_link ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={!farmData?.map_link}
          >
            View Map
          </button>
        </div>
      </div>
      
    </div>
  );
}

export default FarmDetails;
